import { Stack, TextField } from "@fluentui/react";
import { useEffect, useRef, useState, type SyntheticEvent } from "react";
import { PrimaryButtonValidation } from "../atoms/primary-button-validation";
import type { SettingUpdate } from "../../api";
import type { UUID } from "../../utils/uuid";
import { createUuid } from "../../utils/uuid";
import { doubleBindNullableStringEmptyAsNull } from "../../utils/double-bind";
import { observer } from "mobx-react";
import { clone } from "ramda";
import sizes from "../sizes.scss";

import { store } from "../../store";

export const FormUpdateSSO = observer(() => {
    const validationId = useRef(createUuid()).current;
    const [settingId, setSettingId] = useState<UUID | undefined>();
    const [isUpdated, setIsUpdated] = useState(false);
    const [setting, setSetting] = useState<SettingUpdate | undefined>();

    const onChangeSsoSettings = (): void => {
        store.repositorySettings.validation.update.updateModel(validationId, clone(setting)!);
        setIsUpdated(true);
    };

    useEffect(() => {
        const loadSetting = async (): Promise<void> => {
            // We can always assume that the API will return exactly one setting object.
            const [setting] = await store.repositorySettings.byQueryAsync();
            setSettingId(setting.id);
            store.repositorySettings.validation.update.initializeModel(
                validationId,
                clone(setting),
                setting.id,
            );
            setSetting(setting);
        };

        if (settingId == null) {
            loadSetting();
        }

        return () => {
            if (settingId != null) {
                store.repositorySettings.discardMutableCopy(FormUpdateSSO.name, settingId);
            }
        };
    }, [settingId, validationId]);

    const updateSetting = async (evt: SyntheticEvent<HTMLFormElement>): Promise<void> => {
        if (settingId === undefined) {
            throw new Error("submit button should be disabled until setting is loaded");
        }
        evt.preventDefault();
        await store.repositorySettings.update(settingId, clone(setting)!);
        setIsUpdated(false);
    };

    // Require all OIDC settings to be set or none of them.
    const isRequired = (): boolean => {
        return (
            setting?.openidConnectClientId != null ||
            setting?.openidConnectClientSecret != null ||
            setting?.openidConnectIssuerUrl != null
        );
    };

    if (!settingId) {
        return <></>;
    }

    return (
        <form onSubmit={updateSetting}>
            <Stack tokens={{ childrenGap: sizes.m }}>
                <Stack>
                    <TextField
                        label={"Client ID"}
                        {...doubleBindNullableStringEmptyAsNull(
                            setting!,
                            "openidConnectClientId",
                            () => onChangeSsoSettings(),
                        )}
                        required={isRequired()}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositorySettings.validation.update.getFieldValidationState(
                                validationId,
                                "openidConnectClientId",
                            ),
                        )}
                    />
                    <TextField
                        label={"Client Secret"}
                        {...doubleBindNullableStringEmptyAsNull(
                            setting!,
                            "openidConnectClientSecret",
                            () => onChangeSsoSettings(),
                        )}
                        required={isRequired()}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositorySettings.validation.update.getFieldValidationState(
                                validationId,
                                "openidConnectClientSecret",
                            ),
                        )}
                        type="password"
                        canRevealPassword
                    />
                    <TextField
                        label={"Issuer URL"}
                        {...doubleBindNullableStringEmptyAsNull(
                            setting!,
                            "openidConnectIssuerUrl",
                            () => onChangeSsoSettings(),
                        )}
                        required={isRequired()}
                        errorMessage={store.i18n.formatFieldValidationState(
                            store.repositorySettings.validation.update.getFieldValidationState(
                                validationId,
                                "openidConnectIssuerUrl",
                            ),
                        )}
                    />
                    <TextField
                        label={"Redirect URL"}
                        value={`https://${window.location.host}/api/v1/login_tokens/oidc/redirect-uri`}
                        readOnly
                    />
                    <TextField
                        label={"Front-channel Logout URL"}
                        value={`https://${window.location.host}/api/v1/login_tokens/oidc/front-channel-logout`}
                        readOnly
                    />
                </Stack>
                <Stack horizontal horizontalAlign="end">
                    <PrimaryButtonValidation
                        text={store.i18n.t("formUpdateSso.submit.text")}
                        validation={store.repositorySettings.validation.update}
                        validationId={validationId}
                        primaryDisabled={!isUpdated}
                    />
                </Stack>
            </Stack>
        </form>
    );
});
